import './styles.css';
import { ChatwootBridge, type DevFallback, W3sendHostProvider } from '@waforms/app-sdk/client';
import { ToastProvider } from '@waforms/ui';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App.tsx';
import { config } from './lib/config.ts';
import { handleUnsupportedAction, HostToastRelay } from './lib/host-toasts.ts';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      retry: (count, error) => count < 2 && !('status' in error && error.status === 404),
    },
  },
});

// Opened directly (not framed) in development: borrow a session for user 1 / account 1 from
// mock-w3send. Compiled out of production builds.
const devFallback: DevFallback | undefined = import.meta.env.DEV
  ? {
      getToken: async () => {
        const mock = (import.meta.env.VITE_MOCK_W3SEND_URL as string | undefined) ?? 'http://localhost:4400';
        const response = await fetch(`${mock}/dev/session-token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ app: config.appKey, account_id: 1, user_id: 1 }),
        });
        if (!response.ok) throw new Error(`mock-w3send answered ${response.status}`);
        return ((await response.json()) as { token: string }).token;
      },
      context: {
        account: { id: 1, name: 'Acme Dental (dev)' },
        currentAgent: { id: 1, name: 'Asha Menon', email: 'asha@acme.test', role: 'administrator' },
        contact: { id: 1 },
        conversation: { id: 1, inbox_id: 1 },
      },
    }
  : undefined;

// A stock Chatwoot-based CRM (the Zavis CRM) sends its own sign-in instead of an app token; the
// Chatwoot bridge exchanges it for a session at /api/host/session. Temporary (SESSION_HANDOFF=on):
// the conversation tab, which gets no sign-in from the CRM, uses a handoff stored by the sidebar.
const chatwootBridge =
  config.hostKind === 'chatwoot'
    ? new ChatwootBridge({
        allowedOrigins: config.dashboardOrigins,
        sessionUrl: `${config.basePath}/api/host/session`,
        onUnsupportedAction: handleUnsupportedAction,
        ...(config.sessionHandoff
          ? {
              handoff: {
                issueUrl: `${config.basePath}/api/host/handoff`,
                sessionUrl: `${config.basePath}/api/host/session-from-handoff`,
                missingMessage: 'Open WhatsApp Forms from the sidebar once, then reload this tab.',
              },
            }
          : {}),
      })
    : undefined;

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <W3sendHostProvider
      allowedOrigins={config.dashboardOrigins}
      bridge={chatwootBridge}
      devFallback={devFallback}
      apiBaseUrl={`${config.basePath}/api`}
    >
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <HostToastRelay />
          <App />
        </ToastProvider>
      </QueryClientProvider>
    </W3sendHostProvider>
  </StrictMode>,
);
